import { useState, useEffect, type FormEvent } from 'react';
import { getAllProviders, fetchOllamaModels } from '@publishweaver/ai-providers';
import { useAiSettings } from '@/hooks/useAiSettings';

interface Props {
  onBack: () => void;
}

export const StudioSettingsPage = ({ onBack }: Props) => {
  const { settings, updateSettings } = useAiSettings();
  const [ollamaModels, setOllamaModels] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const providers = getAllProviders();

  const selectedProvider = providers.find((p) => p.meta.id === settings.providerId);

  useEffect(() => {
    if (settings.providerId !== 'ollama') return;
    fetchOllamaModels(settings.ollamaBaseUrl || undefined).then((models) => {
      setOllamaModels(models);
    }).catch(() => { setOllamaModels([]); });
  }, [settings.providerId, settings.ollamaBaseUrl]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSaved(true);
    setTimeout(() => { setSaved(false); }, 2000);
  };

  const modelOptions = settings.providerId === 'ollama'
    ? (ollamaModels.length > 0 ? ollamaModels : selectedProvider?.meta.models ?? [])
    : (selectedProvider?.meta.models ?? []);

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <span className="admin-topbar__brand">Studio — Settings</span>
        <button className="admin-btn admin-btn--ghost" onClick={onBack}>Back</button>
      </header>

      <main className="admin-main">
        <div className="admin-section-header">
          <h2 className="admin-section-title">AI Provider</h2>
        </div>

        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="admin-field">
            <label htmlFor="provider" className="admin-label">Provider</label>
            <select
              id="provider"
              className="admin-input"
              value={settings.providerId}
              onChange={(e) => { updateSettings({ providerId: e.target.value, model: '' }); }}
            >
              {providers.map((p) => (
                <option key={p.meta.id} value={p.meta.id}>{p.meta.label}</option>
              ))}
            </select>
          </div>

          {settings.providerId !== 'stub' && (
            <>
              {settings.providerId !== 'ollama' && (
                <div className="admin-field">
                  <label htmlFor="apiKey" className="admin-label">API Key</label>
                  <input
                    id="apiKey"
                    type="password"
                    className="admin-input admin-input--mono"
                    value={settings.apiKey}
                    onChange={(e) => { updateSettings({ apiKey: e.target.value }); }}
                    placeholder="sk-..."
                    autoComplete="off"
                  />
                </div>
              )}

              {settings.providerId === 'ollama' && (
                <div className="admin-field">
                  <label htmlFor="ollamaUrl" className="admin-label">Ollama Base URL</label>
                  <input
                    id="ollamaUrl"
                    type="url"
                    className="admin-input admin-input--mono"
                    value={settings.ollamaBaseUrl}
                    onChange={(e) => { updateSettings({ ollamaBaseUrl: e.target.value }); }}
                  />
                </div>
              )}

              <div className="admin-field">
                <label htmlFor="model" className="admin-label">Model</label>
                <select
                  id="model"
                  className="admin-input"
                  value={settings.model || (selectedProvider?.meta.defaultModel ?? '')}
                  onChange={(e) => { updateSettings({ model: e.target.value }); }}
                >
                  {modelOptions.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                {settings.providerId === 'ollama' && ollamaModels.length === 0 && (
                  <span className="admin-settings__hint">
                    No models found — ensure Ollama is running and models are pulled.
                  </span>
                )}
              </div>
            </>
          )}

          <div className="admin-form-actions">
            <button type="submit" className="admin-btn">
              {saved ? 'Saved!' : 'Save settings'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};
